<template>
  <div class="search-block-wrap">
    <div
      v-if="collapsible && !isExpanded"
      class="search-block-collapsed"
      @click="expand"
    >
      <div v-if="hasCollapsedValue" class="search-block-collapsed__field-label">
        {{ placeholder || 'Search' }}
      </div>
      <div
        v-if="multiple && selectedLabels.length"
        class="search-block-collapsed__multi"
      >
        <div class="search-block-collapsed__chips" @click.stop>
          <v-chip
            v-for="sel in selectedLabels"
            :key="sel.value"
            small
            close
            color="appMain"
            text-color="white"
            class="mr-1 mb-1"
            @click:close="removeMultiValue(sel.value)"
          >
            {{ sel.label }}
          </v-chip>
        </div>
        <v-icon small class="search-block-collapsed__add-icon">add</v-icon>
      </div>
      <div
        v-else-if="!multiple && selectedOption"
        class="search-block-collapsed__selected"
      >
        <v-icon small class="mr-2" color="appMain">check_circle</v-icon>
        <span class="search-block-collapsed__label">{{
          selectedOption.label
        }}</span>
        <v-btn
          icon
          x-small
          class="search-block-collapsed__clear"
          @click.stop="clearValue"
        >
          <v-icon small>close</v-icon>
        </v-btn>
      </div>
      <div v-else class="search-block-collapsed__empty">
        <v-icon small class="mr-2">search</v-icon>
        <span class="search-block-collapsed__placeholder">
          {{ placeholder || 'Search' }}
        </span>
      </div>
    </div>

    <div
      v-else
      class="search-block"
      :class="{ 'search-block--arrow-nav': arrowsNavigation }"
      v-click-outside="clickOutsideConfig"
    >
      <div class="search-input__wrap">
        <v-text-field
          ref="searchInput"
          class="search-input dark-input"
          prepend-icon="search"
          clearable
          clear-icon="cancel"
          :placeholder="placeholder"
          :hint="hint"
          :rules="checkRules()"
          :value="search"
          :loading="loading"
          :autofocus="autofocus || collapsible"
          @input="setSearch"
          @keyup.enter="addNewOption"
        />
        <v-btn
          v-if="showAddButton"
          color="appMain"
          class="ml-2"
          @click="addNewOption"
        >
          Add
        </v-btn>
      </div>

      <div
        v-if="value && selectedOption"
        class="list-field__item active selected light--text mt-1 mb-1"
      >
        <span class="list-field__item-text">
          Selected: {{ selectedOption.label }}
        </span>
        <slot name="selectedBadge" />
      </div>

      <template v-if="noResultsFound">
        <slot name="notFound" v-bind:search="search"></slot>
      </template>

      <transition name="slide">
        <list-field
          v-if="options.length"
          :value="value"
          :items="filteredOptions"
          :showIcons="showIcons"
          :multiple="multiple"
          :expandable="expandable"
          :showSelected="showSelected"
          :disableUnselect="disableUnselect"
          :arrowsNavigation="arrowsNavigation"
          :selected-first="selectedFirst"
          :view-route="viewRoute"
          :visibleCount="visibleCount"
          @input="chooseOption"
        />
      </transition>

      <slot name="action" />
    </div>

    <v-text-field
      v-if="showCollapsedValidation"
      class="validation-input mt-2"
      tabindex="-1"
      :value="value"
      :rules="rules"
    />
  </div>
</template>

<script>
import ListField from '../list-field/ListField';
import { prepareTag } from '@/filters/helpers';

export default {
  name: 'SearchBlock',
  components: {
    ListField,
  },
  data() {
    return {
      search: '',
      isExpanded: false,
    };
  },
  props: {
    value: {
      type: [String, Number, Array],
    },
    options: {
      type: Array,
      default: () => [],
    },
    rules: {
      type: Array,
      default: () => [],
    },
    viewRoute: Object,
    expandable: {
      type: Boolean,
      default: true,
    },
    collapsible: {
      type: Boolean,
      default: false,
    },
    hint: String,
    placeholder: String,
    fieldName: String,
    autofocus: Boolean,
    selectedFirst: Boolean,
    arrowsNavigation: Boolean,
    multiple: Boolean,
    showIcons: Boolean,
    showSelected: Boolean,
    disableUnselect: Boolean,
    addOption: [Boolean, Function],
    visibleCount: Number,
    loading: Boolean,
  },
  computed: {
    filteredOptions() {
      return this.options.filter((i) => {
        const { label, value } = i || {};
        return this.compareStrings(label) || this.compareStrings(value);
      });
    },

    selectedOption() {
      return this.findSelected(this.options, this.value);
    },

    selectedLabels() {
      if (!Array.isArray(this.value)) return [];
      return this.value.map((val) => {
        const match = this.findSelected(this.options, val);
        return { value: val, label: match ? match.label : val };
      });
    },

    hasCollapsedValue() {
      if (this.multiple) return this.selectedLabels.length > 0;
      return !!this.selectedOption;
    },

    clickOutsideConfig() {
      return {
        handler: this.collapse,
        closeConditional: () => this.collapsible && this.isExpanded,
      };
    },

    showCollapsedValidation() {
      return this.collapsible && !this.isExpanded && !!this.rules?.length && !this.hasCollapsedValue;
    },

    showAddButton() {
      return this.addOption && this.noResultsFound;
    },

    noResultsFound() {
      const search = (this.search ?? '').trim();
      return search && !this.filteredOptions.length;
    },
  },
  methods: {
    checkRules() {
      if (!this.rules || !this.rules.length) return [];
      return this.rules.map((rule) => {
        return rule(this.value);
      });
    },

    findSelected(arr, val) {
      return arr.reduce((acc, item) => {
        if (acc) return acc;
        if (item.value === val) return item;
        if (item?.items?.length) return this.findSelected(item.items, val);
      }, null);
    },

    chooseOption(val) {
      this.$emit('input', val);
      if (this.collapsible && !this.multiple) {
        this.$nextTick(() => this.collapse());
      }
    },

    expand() {
      this.isExpanded = true;
      this.$nextTick(() => {
        const input = this.$refs.searchInput?.$el?.querySelector('input');
        if (input) input.focus();
      });
    },

    collapse() {
      this.isExpanded = false;
    },

    clearValue() {
      this.$emit('input', this.multiple ? [] : '');
    },

    removeMultiValue(val) {
      if (!Array.isArray(this.value)) return;
      this.$emit(
        'input',
        this.value.filter((v) => v !== val)
      );
    },

    async addNewOption() {
      if (!this.showAddButton) return;
      let option = this.search.trim();

      if (typeof this.addOption === 'function') {
        this.addOption(option);
        await this.$nextTick();
      } else {
        if (this.fieldName === 'tags') {
          option = prepareTag(option);
        }
        this.value.push(option);
      }

      this.setSearch('');
    },

    compareStrings(fullString = '') {
      return (fullString + '')
        .toLowerCase()
        .includes((this.search || '').toLowerCase());
    },

    setSearch(val) {
      this.search = val;
      this.$emit('updateSearch', val);
    },
  },
};
</script>

<style lang="less">
.search-block-collapsed {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 8px 12px;
  border-radius: @base-radius;
  background-color: #fff;
  box-shadow: @content-shadow;
  cursor: pointer;
  min-height: 40px;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  }

  &__field-label {
    width: 100%;
    font-size: 10px;
    line-height: 1;
    color: var(--v-dark-base);
    opacity: 0.55;
    margin-bottom: 4px;
    font-weight: 600;
  }

  &__multi {
    display: flex;
    align-items: center;
    width: 100%;
    flex-wrap: wrap;
    gap: 2px;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    flex: 1;
  }

  &__add-icon {
    color: var(--v-dark-base) !important;
    opacity: 0.55;
    margin-left: 4px;
  }

  &__selected {
    display: flex;
    align-items: center;
    width: 100%;
  }

  &__label {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    color: var(--v-appMain-base);
  }

  &__clear {
    opacity: 0.5;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 1;
    }
  }

  &__empty {
    display: flex;
    align-items: center;
    width: 100%;
    color: var(--v-dark-base);
    opacity: 0.55;

    .v-icon {
      color: var(--v-dark-base) !important;
    }
  }

  &__placeholder {
    font-size: 14px;
  }
}

.search-block {
  padding: 12px;
  border-radius: @base-radius;
  overflow: hidden;
  background-color: #fff;
  box-shadow: @content-shadow;

  & > .v-btn.v-btn--text {
    width: 100%;
    padding: 0 14px !important;
    margin-left: 26px;

    .v-btn__content {
      justify-content: left;
      color: var(--v-primary-base);
    }
  }

  .v-text-field__details {
    display: block !important;
    min-height: 12px;

    .v-messages {
      min-height: 12px;
    }

    .v-messages__message {
      margin-top: 6px;
    }
  }

  div.search-input.v-input {
    .v-input__slot {
      padding: 4px 15px 4px 40px;
      box-shadow: none;
    }

    .v-input__prepend-outer {
      top: 5px;
      left: 8px;

      .v-icon {
        color: var(--v-appMain-base) !important;
      }
    }

    .v-input__append-inner {
      margin-top: 4px;
      margin-right: -5px;

      .v-icon {
        color: var(--v-appMain-base) !important;
      }
    }
  }

  .list-field {
    margin-left: 27px;
    padding: 3px 0 0;
  }

  .list-field__item {
    font-size: @font-size-base;
    padding: 6px 12px;
    border-radius: @base-radius;

    &.active {
      background: var(--v-appMain-base);

      .v-icon,
      .list-field__item-text {
        color: var(--v-mainInvert-base) !important;
      }
    }

    & + .list-field__item {
      margin-top: 2px;
    }
  }

  &.search-block--arrow-nav {
    flex: 1;
    display: flex;
    flex-direction: column;

    .list-field {
      overflow: auto;
      flex: 1;
      position: relative;
    }
  }
}

.search-list {
  &__item {
    background-color: #fff;
  }
}

.search-input__wrap {
  display: flex;

  .v-btn {
    height: 40px !important;
    margin-left: 4px;
  }
}
</style>
